/**
 * ResetPasswordPage — Part B of the password-reset flow.
 *
 * Mounted at `/reset-password?token=<raw>`.
 *
 * Lifecycle:
 *   1. On mount, calls `GET /api/auth/reset-password/validate?token=<raw>`
 *      to pre-flight the token so we render the right state without
 *      making the user type a new password against a dead link.
 *   2. Renders one of `{loading, valid form, invalid, expired,
 *      consumed, invalidated}` based on the validate response.
 *      If the URL has no `?token=`, we skip the pre-flight and render
 *      the `invalid` state directly.
 *   3. On submit of the form we call `POST /api/auth/reset-password`
 *      with `{token, new_password}`. On 200 we toast a success message
 *      + navigate to `/login`. On 400 with a resolver reason we flip
 *      to the matching dead-token state (server-authoritative — the
 *      client's stored `phase` was stale).
 *
 * Client-side validation mirrors the server's Pydantic rule
 * (`new_password` min length = 8) plus a "confirm password" match check
 * so the mistyped-second-field footgun is caught pre-submit.
 *
 * No hex colors, no `prefers-color-scheme` reads — all styling uses the
 * existing token classes (`bg-pp-surface`, `text-pp-text`,
 * `bg-pp-purple`, `text-brand-orange`) so the Family L guard is happy.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import PublicPageShell from "@/components/PublicPageShell";
import { Loader2, ShieldAlert, TimerOff, Ban, RotateCcw } from "lucide-react";

const MIN_PASSWORD_LEN = 8;

// Copy indexed by resolver reason. Kept close to the frontend so the
// spec-required states are impossible to accidentally omit.
const DEAD_STATE_COPY = {
  invalid: {
    heading: "Reset link not recognized",
    body:
      "This reset link doesn't look right. Double-check the URL from your email, or request a new one below.",
    Icon: ShieldAlert,
    testid: "reset-password-state-invalid",
  },
  expired: {
    heading: "Reset link expired",
    body:
      "For security, reset links only work for 60 minutes. Request a fresh one and try again.",
    Icon: TimerOff,
    testid: "reset-password-state-expired",
  },
  consumed: {
    heading: "Reset link already used",
    body:
      "This link was already used to set a new password. If that wasn't you, request a new link and reset again.",
    Icon: RotateCcw,
    testid: "reset-password-state-consumed",
  },
  invalidated: {
    heading: "Reset link superseded",
    body:
      "A newer reset link was issued after this one. Please use the most recent email — older links stop working automatically.",
    Icon: Ban,
    testid: "reset-password-state-invalidated",
  },
};

function DeadStateCard({ reason }) {
  const meta = DEAD_STATE_COPY[reason] || DEAD_STATE_COPY.invalid;
  const Icon = meta.Icon;
  return (
    <div
      className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
      data-testid="reset-password-dead-card"
    >
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full bg-pp-surface-2 p-4" aria-hidden="true">
          <Icon className="h-8 w-8 text-brand-orange" />
        </div>
        <h2
          className="mt-4 font-heading text-2xl font-bold text-pp-text"
          data-testid={meta.testid}
        >
          {meta.heading}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">{meta.body}</p>
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <Link
          to="/forgot-password"
          className="inline-flex h-12 w-full items-center justify-center rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
          data-testid="reset-password-request-new-link"
        >
          Request a new link
        </Link>
        <Link
          to="/login"
          className="text-center text-sm font-semibold text-brand-orange hover:underline"
          data-testid="reset-password-back-to-login"
        >
          Back to log in
        </Link>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rawToken = params.get("token") || "";

  // `phase` is the state machine: loading | valid | invalid | expired |
  // consumed | invalidated | submitting | done.
  // `done` is transient (immediately followed by a nav to /login) —
  // rendered as a small success card while the redirect settles.
  const [phase, setPhase] = useState(rawToken ? "loading" : "invalid");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Guard against React 18 StrictMode double-fire.
  const validateFired = useRef(false);

  useEffect(() => {
    if (validateFired.current) return;
    validateFired.current = true;
    if (!rawToken) return; // already in `invalid` phase from initial state
    (async () => {
      try {
        const resp = await api.get(
          `/auth/reset-password/validate?token=${encodeURIComponent(rawToken)}`,
        );
        if (resp?.data?.valid) {
          setPhase("valid");
        } else {
          const reason = resp?.data?.reason;
          setPhase(DEAD_STATE_COPY[reason] ? reason : "invalid");
        }
      } catch (err) {
        // 429 rate-limit or network failure — surface as invalid so the
        // user can back off + retry via "Request a new link".
        setPhase("invalid");
      }
    })();
  }, [rawToken]);

  const lengthOk = newPassword.length >= MIN_PASSWORD_LEN;
  const matchOk = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = lengthOk && matchOk && phase === "valid";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase("submitting");
    try {
      await api.post("/auth/reset-password", {
        token: rawToken,
        new_password: newPassword,
      });
      setPhase("done");
      toast.success("Password updated. Please log in with your new password.");
      // Small delay so the success card is visible before the redirect.
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Server-authoritative dead-token flip. If the resolver reports
      // a specific reason (invalid|expired|consumed|invalidated) we
      // switch the page to that state so the copy matches reality.
      if (status === 400 && typeof detail === "string" && DEAD_STATE_COPY[detail]) {
        setPhase(detail);
        return;
      }
      // Unknown-shape error — fall back to the friendlier "valid"
      // state with a toast so the user can retry.
      setPhase("valid");
      toast.error(formatApiErrorDetail(detail) || "Something went wrong. Please try again.");
    }
  };

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" data-testid="reset-password-logo-link" className="block">
            <Logo variant="full" className="h-32 w-32" />
          </Link>
          <h1
            className="mt-2 font-heading text-3xl font-bold text-pp-text"
            data-testid="reset-password-heading"
          >
            Set a new password
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick something you'll remember — at least 8 characters.
          </p>
        </div>

        {phase === "loading" && (
          <div
            className="flex flex-col items-center justify-center rounded-3xl border border-border bg-pp-surface p-10 shadow-sm"
            data-testid="reset-password-loading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-brand-orange" aria-hidden="true" />
            <p className="mt-3 text-sm text-muted-foreground">Checking your link…</p>
          </div>
        )}

        {(phase === "invalid" ||
          phase === "expired" ||
          phase === "consumed" ||
          phase === "invalidated") && <DeadStateCard reason={phase} />}

        {(phase === "valid" || phase === "submitting") && (
          <div className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm">
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              data-testid="reset-password-form"
            >
              <div>
                <Label htmlFor="rp-new-password" className="text-sm font-semibold text-pp-text">
                  New password
                </Label>
                <Input
                  id="rp-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                  autoComplete="new-password"
                  autoFocus
                  required
                  minLength={MIN_PASSWORD_LEN}
                  data-testid="reset-password-new-input"
                />
                {newPassword.length > 0 && !lengthOk && (
                  <p
                    className="mt-1 text-xs text-brand-orange"
                    data-testid="reset-password-length-error"
                  >
                    Must be at least {MIN_PASSWORD_LEN} characters.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="rp-confirm-password" className="text-sm font-semibold text-pp-text">
                  Confirm new password
                </Label>
                <Input
                  id="rp-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retype it"
                  className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LEN}
                  data-testid="reset-password-confirm-input"
                />
                {confirmPassword.length > 0 && !matchOk && (
                  <p
                    className="mt-1 text-xs text-brand-orange"
                    data-testid="reset-password-match-error"
                  >
                    Passwords don't match.
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={!canSubmit || phase === "submitting"}
                className="h-12 w-full rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
                data-testid="reset-password-submit-button"
              >
                {phase === "submitting" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save new password"
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Remembered it after all?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-brand-orange hover:underline"
                  data-testid="reset-password-back-to-login"
                >
                  Back to log in
                </Link>
              </p>
            </form>
          </div>
        )}

        {phase === "done" && (
          <div
            className="rounded-3xl border border-border bg-pp-surface p-8 text-center shadow-sm"
            data-testid="reset-password-done"
          >
            <p className="text-2xl" aria-hidden="true">🐾</p>
            <p className="mt-3 text-base font-semibold text-pp-text">
              Password updated
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Redirecting you to log in with your new password…
            </p>
          </div>
        )}
      </div>
    </PublicPageShell>
  );
}
